/**
 * L'entrée dans le live, ce qui s'en décide sans écran : prévenir l'équipe ou non, effacer le
 * souvenir ou non, et dire comment s'est fini l'essai d'entrer.
 *
 * Les deux premières sont les règles de l'ancien `PreCallScreen` (`alertTechnician`), reprises
 * telles quelles : l'élève entre avec un voyant mesuré qui n'est pas au vert, l'équipe est prévenue
 * en silence, avec ce qu'il a déjà essayé ; et le souvenir s'efface si c'est le MATÉRIEL qui cloche
 * (pas un Wi-Fi capricieux, ni une vidéo coupée par l'élève).
 *
 * La troisième est nouvelle : aujourd'hui, un élève du roster sur cinq n'entre jamais dans sa séance,
 * et l'on ne sait pas s'il a essayé. L'issue de chaque essai part avec l'événement `precall.join`
 * déjà admis dans Amplitude (`outcome`) : un nouveau type d'événement n'y serait pas interrogeable.
 */
import { ISSUE_REPORT_CATEGORY, ISSUE_REPORT_SOURCE } from "../../data/constants/issueReportConstants"
import { type DeviceCheckReport, type DevicePermission } from "./deviceCheckMachine"
import { alertCategory, materialBilanForMemory, shouldAlertTechnician } from "./deviceCheckRules"

/**
 * Comment s'est fini un essai d'entrer :
 *  - `entered` : l'élève est entré (le live s'ouvre) ;
 *  - `permission_denied` : il est reparti avec le micro ou la caméra refusés ;
 *  - `network_blocked` : il est reparti avec une connexion mesurée au rouge ;
 *  - `abandoned` : il est reparti, sans qu'aucun des deux ne l'explique.
 */
export type LiveEntryOutcome = "entered" | "abandoned" | "permission_denied" | "network_blocked"

export function entryOutcome(args: {
    entered: boolean
    report: DeviceCheckReport
    permissions: { microphone: DevicePermission; camera: DevicePermission }
}): LiveEntryOutcome {
    const { entered, report, permissions } = args
    if (entered) return "entered"
    if (permissions.microphone === "denied" || permissions.camera === "denied") return "permission_denied"
    if (report.network === "bad") return "network_blocked"
    return "abandoned"
}

/**
 * Les propriétés de `precall.join` que l'ancien `PreCallScreen` envoyait (`:320-329`) : `all_done` et les
 * quatre verdicts acquis. Les tableaux lisent ces clés telles quelles ; le précall du masteur n'envoie qu'elles.
 */
export function reportVerdicts(
    report: Pick<DeviceCheckReport, "allDone" | "microphone" | "speaker" | "camera" | "network">
): Record<string, string | boolean | null> {
    return {
        all_done: !!report.allDone,
        microphone: report.microphone ?? null,
        speaker: report.speaker ?? null,
        camera: report.camera ?? null,
        network: report.network ?? null,
    }
}

/** Les propriétés de `precall.join` : l'issue, et les quatre verdicts acquis au moment de partir. */
export function entryTracking(args: {
    outcome: LiveEntryOutcome
    report: DeviceCheckReport
    permissions: { microphone: DevicePermission; camera: DevicePermission }
    /** Sam attendait l'élève (le starting block était prêt). */
    startingBlock: boolean
}): Record<string, string | boolean | null> {
    const { outcome, report, permissions, startingBlock } = args
    return {
        outcome,
        ...reportVerdicts(report),
        microphone_permission: permissions.microphone,
        camera_permission: permissions.camera,
        starting_block: startingBlock,
    }
}

/** Les propriétés de `precall.join`, lues sur la vue du test : l'issue de l'essai et ce qui l'explique. */
export function joinTracking(
    view: { report: DeviceCheckReport; permissions: { microphone: DevicePermission; camera: DevicePermission } },
    entered: boolean,
    startingBlock: boolean
): Record<string, string | boolean | null> {
    const { report, permissions } = view
    return entryTracking({
        outcome: entryOutcome({ entered, report, permissions }),
        report,
        permissions,
        startingBlock,
    })
}

/** Le signalement à l'équipe, quand il y en a un : ce que `submitIssueReport` reçoit. */
export type TechnicianAlert = {
    /** Le matériel cloche : le souvenir s'efface, la prochaine entrée repasse par le parcours. */
    forgetMemory: boolean
    category: string
    payload: {
        category: string
        description: string
        source: string
        attempts: string[]
        verdicts: { microphone: string; speaker: string; camera: string; network: string }
        diagnostics: DeviceCheckReport["diagnostics"]
        platform: string
    }
}

/**
 * Le signalement, s'il doit partir : tout voyant MESURÉ non vert prévient l'équipe (arbitrage du
 * 26/08, borné aux mesures le 10/09). Le filet ne vaut que pour l'élève : c'est l'appelant qui
 * décide qui il sert.
 */
export function technicianAlert(
    report: DeviceCheckReport,
    context: { isVideoMuted: boolean; platform: string }
): TechnicianAlert | null {
    if (!shouldAlertTechnician(report)) return null
    const category = (alertCategory(report) as string | null) ?? ISSUE_REPORT_CATEGORY.OTHER
    return {
        forgetMemory: shouldAlertTechnician(materialBilanForMemory(report, { isVideoMuted: context.isVideoMuted })),
        category,
        payload: {
            category,
            description: "Entrée au live sans que tout soit au vert, détectée par le test matériel d'entrée.",
            source: ISSUE_REPORT_SOURCE.PRECALL_CHECK,
            attempts: report.attempts,
            verdicts: {
                microphone: report.microphone,
                speaker: report.speaker,
                camera: report.camera,
                network: report.network,
            },
            diagnostics: report.diagnostics,
            platform: context.platform,
        },
    }
}
